/**
 * Cổng ra storage — Prompt.md §27, §7.
 *
 * Interface nằm ở đây (core) còn cài đặt nằm ở platform: `session-core` không được biết
 * IndexedDB tồn tại, nếu không thì bản React Native phải kéo theo một API của trình duyệt.
 */

import type { StudySession } from "@studystraight/shared-types";

/** Phiên đang chạy, được ghi định kỳ (quyết định #15) để không mất trắng khi tab bị kill. */
export interface SessionSnapshot {
  session: StudySession;
  /** `false` cho tới khi `end()` được gọi — recovery flow chỉ hỏi về snapshot dở dang. */
  finalized: boolean;
  savedAt: number;
}

export interface SessionRepository {
  /**
   * Lưu phiên đã kết thúc.
   *
   * **Cũng phải xoá snapshot dở dang**: phiên vừa lưu chính là phiên mà snapshot đang mô tả, để
   * lại thì lần mở app sau sẽ hỏi "phiên trước bị ngắt, lưu không?" cho một phiên đã lưu rồi.
   */
  save(session: StudySession): Promise<void>;
  getById(id: string): Promise<StudySession | null>;
  /** Mới nhất trước — History (§27) luôn hiển thị theo thứ tự này. */
  getAll(): Promise<readonly StudySession[]>;

  /** Ghi phiên dở dang (quyết định #15). Gọi mỗi `snapshotIntervalMs`. */
  saveSnapshot(snapshot: SessionSnapshot): Promise<void>;
  /** Snapshot chưa finalize còn sót lại từ lần chạy trước, nếu có. */
  getPendingSnapshot(): Promise<SessionSnapshot | null>;
  clearSnapshot(): Promise<void>;

  /**
   * Xoá sạch dữ liệu.
   *
   * Không có trong §27 nhưng là hệ quả trực tiếp của §7: đây là sản phẩm cho trẻ em, phải có
   * đường xoá dữ liệu, và đường đó thuộc repository chứ không phải code UI.
   */
  deleteAll(): Promise<void>;
}

/**
 * Cài đặt in-memory — fallback khi IndexedDB không dùng được (Safari private mode) và là bản
 * dùng trong test. UI phải hiện cảnh báo "lịch sử sẽ không được lưu" khi rơi vào bản này.
 */
export class InMemorySessionRepository implements SessionRepository {
  private readonly sessions = new Map<string, StudySession>();
  private snapshot: SessionSnapshot | null = null;

  save(session: StudySession): Promise<void> {
    this.sessions.set(session.id, session);
    // Giống bản IndexedDB (xem `SessionRepository.save`): bản in-memory không được lệch hành vi,
    // nếu không thì test xanh mà thiết bị thật vẫn hỏi recovery cho phiên đã lưu — hoặc ngược lại.
    this.snapshot = null;
    return Promise.resolve();
  }

  getById(id: string): Promise<StudySession | null> {
    return Promise.resolve(this.sessions.get(id) ?? null);
  }

  getAll(): Promise<readonly StudySession[]> {
    return Promise.resolve([...this.sessions.values()].sort((a, b) => b.startTime - a.startTime));
  }

  saveSnapshot(snapshot: SessionSnapshot): Promise<void> {
    this.snapshot = snapshot;
    return Promise.resolve();
  }

  getPendingSnapshot(): Promise<SessionSnapshot | null> {
    return Promise.resolve(this.snapshot?.finalized === false ? this.snapshot : null);
  }

  clearSnapshot(): Promise<void> {
    this.snapshot = null;
    return Promise.resolve();
  }

  deleteAll(): Promise<void> {
    this.sessions.clear();
    this.snapshot = null;
    return Promise.resolve();
  }
}
